import React, {
    forwardRef,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from "react";
import dynamic from "next/dynamic";

import { LINEUP_CONTENT_DEFAULT_COMPONENT_NAME } from "~constants";
import Ad from "~components/shared/ad";
import { VALID_AD_TYPES } from "~components/shared/ad/utility";
import { devModuleProps } from "~config/devModuleProps";
import { PageContext } from "~context/PageContext";
import {
    mergeWithPaginatedModules,
    fetchPagination,
} from "~redux/features/modulesById-slice";
import useRedux from "~redux/hooks";
import { modulesSelector } from "~redux/selectors";
import {
    type HydratedCurationModule,
    type HydratedModulesById,
    ModuleSource,
    type PaginatedModule,
} from "~types/types";
import isEnabled from "~utils/features";
import { isClient } from "~utils/isClient";

import {
    COMPONENTS_WITH_PAGINATION,
    LINEUP_COMPONENTS,
    LINEUP_INTEGRITY_CHECKS,
} from "../../shared/zones/lineupComponents";
import { setShadowModule } from "../../shared/zones/utility";
import styles from "../../shared/zones/zone.module.css";

import ModuleContext from "./modulecontext";

// Only used with the "preview" flag
const BrokenModule = dynamic(() =>
    import("./brokenModule").then((module) => module.BrokenModule)
);
const PreviewModule = dynamic(() =>
    import("~components/shared/preview").then((module) => module.PreviewModule)
);

export const MODULE_PLACEHOLDERS_ENABLED = isEnabled(
    "modulePlaceholderFallback"
);
const IS_DEV = process.env.NODE_ENV === "development";

// Only used when the "MODULE_PLACEHOLDERS_ENABLED" flag is enabled
const PlaceholderModule = dynamic(() =>
    import("./placeholderModule").then((module) => module.PlaceholderModule)
);

interface ModuleProps {
    module: HydratedCurationModule;
    preview: boolean;
}

const LineupModule = forwardRef<HTMLDivElement, ModuleProps>((props, ref) => {
    const { module, preview } = props;

    const { componentName = "", moduleErrors = [] } = module;

    const { useAppSelector, useAppDispatch } = useRedux();
    const dispatch = useAppDispatch();

    const fetchLock = useRef(false);
    const [paginationLoading, setPaginationLoading] = useState(false);
    const { pageId } = useAppSelector((state) => state.pageData);
    const [paginatedModules, setPaginatedModules] =
        useState<HydratedModulesById>({});

    const getPagination = useCallback(
        async (hydratedModule: PaginatedModule) => {
            const response = await fetchPagination({
                module: hydratedModule,
                pageId,
            });

            return response;
        },
        [pageId]
    );

    // Callback for any modules with pagination
    const onRequestMergePaginate = useCallback(
        (hydratedModule: PaginatedModule) => {
            if (paginatedModules) {
                dispatch(
                    mergeWithPaginatedModules({
                        module: hydratedModule,
                        paginatedModules,
                    })
                );
            }
        },
        [paginatedModules, dispatch]
    );

    useEffect(() => {
        if (
            COMPONENTS_WITH_PAGINATION.includes(componentName) &&
            !fetchLock.current
        ) {
            fetchLock.current = true;
            setPaginationLoading(true);
            getPagination(module as PaginatedModule).then((response) => {
                setPaginatedModules(response);
                setPaginationLoading(false);
                fetchLock.current = false;
            });
        }
    }, [module]);

    const ChosenComponent = LINEUP_COMPONENTS[componentName];
    const integrityName = `${componentName}Integrity`;
    const integrityCheck = LINEUP_INTEGRITY_CHECKS[integrityName];

    const integrityResponse =
        "function" === typeof integrityCheck ? integrityCheck(module) : [];

    if (COMPONENTS_WITH_PAGINATION.includes(componentName)) {
        const paginationAvailable =
            Boolean(paginatedModules[module.id]?.items?.length) &&
            module.items.length === module.nextItemOffset;

        return (
            <div ref={ref}>
                <ChosenComponent
                    {...module}
                    onRequestPaginate={() =>
                        onRequestMergePaginate(module as PaginatedModule)
                    }
                    paginationAvailable={paginationAvailable}
                    paginationLoading={paginationLoading}
                />
            </div>
        );
    }

    if (integrityResponse?.length > 0 || moduleErrors?.length > 0) {
        // Integrity failures for shadow modules would get caught by their canonical counterparts
        if (module.source === ModuleSource.Shadow) {
            return null;
        }

        const integrityErrors = integrityResponse.join(", ");
        const errorMessage = `${componentName} (id: ${module.id}) on ${pageId} failed its integrity check due to: ${integrityErrors}`;

        if (isClient() && integrityErrors?.length > 0) {
            if (!preview) {
                window?.newrelic?.noticeError(new Error(errorMessage));
            }
        } else {
            console.error(errorMessage);
        }

        if (preview) {
            return (
                <div ref={ref}>
                    <BrokenModule
                        pageId={pageId}
                        moduleId={module.id}
                        componentName={componentName}
                        integrityErrors={integrityResponse}
                        moduleErrors={moduleErrors}
                    />
                </div>
            );
        }

        if (isEnabled("throw500ForInvalidModules") && !isClient()) {
            // this error thrown here will 500 the page
            // the CDN in prod will serve the stale page when it receives a 500
            throw new Error(errorMessage);
        }

        if (isEnabled("hideInvalidModules")) {
            return null;
        }
    }

    return (
        <ModuleContext.Provider value={{ componentName }}>
            <ChosenComponent {...module} ref={ref} />
        </ModuleContext.Provider>
    );
});

LineupModule.displayName = "LineupModule";

export const Module = forwardRef<HTMLDivElement, ModuleProps>((props, ref) => {
    let { module } = props;
    const { preview } = props;

    if (IS_DEV) {
        module = devModuleProps(module);
    }

    const { useAppSelector } = useRedux();

    const modules = useAppSelector(modulesSelector);
    const { useGrid } = useContext(PageContext);

    let _module = module;

    if (module.duplicateOf) {
        const canonicalModule = modules[module.duplicateOf];

        if (!canonicalModule) return null;

        _module = setShadowModule(module, canonicalModule);
    }

    // Render Ad if applicable
    if ("ad" === _module.type && _module.adType) {
        if (!VALID_AD_TYPES.has(_module.adType)) return null;

        const ad = (
            <Ad
                type={_module.adType}
                pageId={_module.pageId}
                positionIncrement={_module.adPositionIncrement || undefined}
            />
        );

        if (useGrid) {
            return ad;
        }

        return <div className={styles.borderless}>{ad}</div>;
    }

    // Render lineup component if one exists
    const { componentName = "" } = _module;

    if (preview && componentName === LINEUP_CONTENT_DEFAULT_COMPONENT_NAME) {
        return (
            <div ref={ref}>
                <BrokenModule
                    pageId={_module.pageId}
                    moduleId={_module.id}
                    componentName={componentName}
                    integrityErrors={[
                        "This module cannot render because it does not have a Variation. Please select a variation from the dropdown.",
                    ]}
                    moduleErrors={[]}
                />
            </div>
        );
    }

    if (componentName in LINEUP_COMPONENTS) {
        return <LineupModule ref={ref} module={_module} preview={preview} />;
    }

    // Render placeholder fallback
    if (MODULE_PLACEHOLDERS_ENABLED) {
        return <PlaceholderModule module={_module} />;
    }

    return null;
});

Module.displayName = "Module";

export const getModuleComponent = (
    module: HydratedCurationModule,
    preview: boolean,
    isModulePreview?: boolean
) => {
    const { type, id, duplicateOf } = module;

    const moduleComponent = (
        <Module module={module} key={id} preview={preview} />
    );

    if (preview && type !== "ad" && !isModulePreview) {
        return (
            <PreviewModule id={id} canonical={duplicateOf || id}>
                {moduleComponent}
            </PreviewModule>
        );
    }

    return moduleComponent;
};
